import { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@material-ui/core';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import CloseIcon from '@material-ui/icons/Close';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { RebacExplorerClient } from '../../api/RebacExplorerClient';
import type { Grant, GrantRelation } from '../../api/RebacExplorerClient';
import { GrantsGrid } from './GrantsGrid';
import { GrantForm } from './GrantForm';
import { RevokeModal } from './RevokeModal';

interface ManageAccessButtonProps {
  resourceType: string;
  resourceId: string;
  /** Optional label override shown in the drawer subtitle. Defaults to `<resourceType>: <resourceId>`. */
  label?: string;
  /**
   * Quando fornecido pelo pai (ex.: PluginAccessPage), evita chamada extra à API
   * e controla se o botão está habilitado. `undefined` = aguardando API local.
   */
  canManage?: boolean;
}

export function ManageAccessButton({ resourceType, resourceId, label, canManage: canManageProp }: ManageAccessButtonProps) {
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const client = new RebacExplorerClient({ discoveryApi, fetchApi });
  const object = `${resourceType}:${resourceId}`;

  // buttonVisible: true → exibir o botão (pode estar desabilitado)
  // buttonEnabled: true → usuário tem can_manage e pode abrir o drawer
  const [buttonVisible, setButtonVisible] = useState(canManageProp !== undefined);
  const [buttonEnabled, setButtonEnabled] = useState(canManageProp ?? false);
  const [open, setOpen] = useState(false);
  const [grants, setGrants] = useState<Grant[]>([]);
  const [canManage, setCanManage] = useState(canManageProp ?? false);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showGrantForm, setShowGrantForm] = useState(false);
  const [crossTenantError, setCrossTenantError] = useState<string | null>(null);
  const [grantError, setGrantError] = useState<string | null>(null);
  const [grantSuccess, setGrantSuccess] = useState(false);
  const [toRevoke, setToRevoke] = useState<Grant | null>(null);
  const [availableRelations, setAvailableRelations] = useState<GrantRelation[]>(['viewer', 'editor', 'admin']);

  useEffect(() => {
    // Quando o pai já fornece canManage, não precisamos chamar a API para visibilidade
    if (canManageProp !== undefined) return;
    client.getGrants(resourceType, resourceId)
      .then(data => {
        const visible = data.can_manage || data.can_view;
        setButtonVisible(visible);
        setButtonEnabled(data.can_manage);
      })
      .catch(() => {
        setButtonVisible(false);
        setButtonEnabled(false);
      });
  }, [resourceType, resourceId, canManageProp]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await client.getGrants(resourceType, resourceId);
      setGrants(data.grants);
      setCanManage(data.can_manage);
    } finally {
      setLoading(false);
    }
  }, [resourceType, resourceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpen = () => {
    setOpen(true);
    setExpanded(false);
    setShowGrantForm(false);
    setGrantError(null);
    load();
    client.getRelationsForType(resourceType, resourceId)
      .then(setAvailableRelations)
      .catch(() => {}); // fallback mantém o default
  };

  const handleClose = () => {
    setOpen(false);
    setShowGrantForm(false);
    setGrantError(null);
    setCrossTenantError(null);
  };

  const handleGrant = async (
    target: string,
    relation: import('../../api/RebacExplorerClient').GrantRelation,
    grantDuration?: string,
  ) => {
    setGrantError(null);
    setCrossTenantError(null);
    try {
      await client.grantAccess({ target, relation, object, grantDuration });
      setShowGrantForm(false);
      setGrantSuccess(true);
      setTimeout(() => setGrantSuccess(false), 3000);
      await load();
    } catch (e: any) {
      const msg: string = e.message ?? 'Erro ao conceder acesso';
      if (msg.includes('Cross-tenant') || msg.includes('RN-02')) {
        setCrossTenantError('Delegação entre tenants não é permitida.');
      } else {
        setGrantError(msg);
      }
    }
  };

  const handleRevoke = async () => {
    if (!toRevoke) return;
    try {
      await client.revokeAccess(resourceType, resourceId, { target: toRevoke.user, relation: toRevoke.relation });
      setToRevoke(null);
      await load();
    } catch (e: any) {
      setGrantError(e.message ?? 'Erro ao revogar acesso');
    }
  };

  const directGrants = grants.filter(g => g.origin === 'direct');
  const groupGrants = grants.filter(g => g.origin === 'via_group');
  const tenantGrants = grants.filter(g => g.origin === 'via_tenant');
  const totalCount = grants.length;
  let accessSummary: string | null;
  if (loading) {
    accessSummary = null;
  } else if (directGrants.length === 0 && groupGrants.length === 0 && tenantGrants.length === 0) {
    accessSummary = 'Nenhuma permissão explícita além do Platform Team.';
  } else {
    const parts: string[] = [];
    if (directGrants.length > 0) {
      const s = directGrants.length === 1 ? 'ão direta' : 'ões diretas';
      parts.push(`${directGrants.length} permiss${s}`);
    }
    if (groupGrants.length > 0) {
      const s = groupGrants.length === 1 ? 'grupo' : 'grupos';
      parts.push(`${groupGrants.length} via ${s}`);
    }
    if (tenantGrants.length > 0) {
      parts.push(`${tenantGrants.length} via tenant`);
    }
    accessSummary = parts.join(' · ');
  }

  if (!buttonVisible) return null;

  return (
    <>
      <Tooltip
        title={buttonEnabled ? '' : 'Você tem acesso de visualização. Solicite permissão de admin para gerenciar.'}
      >
        {/* span necessário para o Tooltip funcionar em botão desabilitado */}
        <span>
          <Button
            size="small"
            variant="outlined"
            startIcon={<LockOpenIcon />}
            onClick={buttonEnabled ? handleOpen : undefined}
            disabled={!buttonEnabled}
            style={{ flexShrink: 0, textTransform: 'none', fontWeight: 600 }}
          >
            Gerenciar acesso
          </Button>
        </span>
      </Tooltip>

      <Drawer anchor="right" open={open} onClose={handleClose}>
        <Box style={{ width: 480, display: 'flex', flexDirection: 'column', height: '100%' }}>

          <Box style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <Box>
              <Typography variant="h6" style={{ fontWeight: 700, lineHeight: 1.3 }}>
                Gerenciar acesso
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {label ?? <><code>{resourceType}</code>: <code>{resourceId}</code></>}
              </Typography>
            </Box>
            <IconButton size="small" onClick={handleClose} style={{ marginLeft: 8 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Divider />

          <Box style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>

            <Box style={{ marginBottom: 20 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="body2" color="textSecondary">
                  {accessSummary}
                </Typography>
                {totalCount > 0 && (
                  <Button
                    size="small"
                    onClick={() => setExpanded(v => !v)}
                    endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    style={{ textTransform: 'none', fontSize: 12, color: theme.palette.text.secondary }}
                  >
                    {expanded ? 'Ocultar' : 'Ver quem tem acesso'}
                  </Button>
                )}
              </Box>
              <Collapse in={expanded} unmountOnExit>
                <Box style={{ marginTop: 12 }}>
                  {loading ? (
                    <Box display="flex" justifyContent="center" style={{ padding: 16 }}>
                      <CircularProgress size={20} />
                    </Box>
                  ) : (
                    <>
                      {directGrants.length > 0 && (
                        <>
                          <Typography variant="caption" style={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: theme.palette.text.secondary, display: 'block', marginBottom: 8 }}>
                            Permissões diretas
                          </Typography>
                          <GrantsGrid grants={directGrants} canManage={canManage} canDelete={false} onRevoke={setToRevoke} />
                        </>
                      )}
                      {groupGrants.length > 0 && (
                        <Box style={{ marginTop: directGrants.length > 0 ? 16 : 0 }}>
                          <Typography variant="caption" style={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: theme.palette.text.secondary, display: 'block', marginBottom: 4 }}>
                            Acesso herdado via grupo
                          </Typography>
                          <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 8 }}>
                            Estes acessos são definidos pela participação em grupos — não podem ser revogados aqui.
                          </Typography>
                          <GrantsGrid grants={groupGrants} canManage={false} canDelete={false} onRevoke={setToRevoke} />
                        </Box>
                      )}
                      {tenantGrants.length > 0 && (
                        <Box style={{ marginTop: (directGrants.length > 0 || groupGrants.length > 0) ? 16 : 0 }}>
                          <Typography variant="caption" style={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: theme.palette.text.secondary, display: 'block', marginBottom: 4 }}>
                            Acesso herdado via tenant
                          </Typography>
                          <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 8 }}>
                            Estes usuários têm acesso administrativo pelo papel de <code>superadmin</code> ou <code>owner</code> no tenant{tenantGrants[0]?.tenantName ? ` "${tenantGrants[0].tenantName}"` : ''} — não podem ser revogados aqui.
                          </Typography>
                          <GrantsGrid grants={tenantGrants} canManage={false} canDelete={false} onRevoke={setToRevoke} />
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              </Collapse>
            </Box>

            <Divider style={{ marginBottom: 20 }} />

            {grantSuccess && (
              <Typography variant="body2" style={{ marginBottom: 12, color: isDark ? '#a5d6a7' : '#2e7d32', padding: '8px 12px', borderRadius: 4, border: `1px solid ${theme.palette.success.main}`, backgroundColor: isDark ? 'rgba(76,175,80,0.12)' : '#f1f8e9' }}>
                Acesso concedido com sucesso.
              </Typography>
            )}
            {grantError && (
              <Typography variant="body2" style={{ marginBottom: 12, color: theme.palette.error.main, padding: '8px 12px', borderRadius: 4, border: `1px solid ${theme.palette.error.main}`, backgroundColor: isDark ? 'rgba(211,47,47,0.12)' : '#ffebee' }}>
                {grantError}
              </Typography>
            )}

            {showGrantForm ? (
              <GrantForm
                objectLabel={object}
                availableRelations={availableRelations}
                onSubmit={handleGrant}
                onCancel={() => { setShowGrantForm(false); setGrantError(null); setCrossTenantError(null); }}
                crossTenantError={crossTenantError}
              />
            ) : (
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                  Conceder novo acesso
                </Typography>
                {canManage && (
                  <Button size="small" variant="contained" color="primary" onClick={() => setShowGrantForm(true)}>
                    + Conceder acesso
                  </Button>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Drawer>

      <RevokeModal
        grant={toRevoke}
        objectLabel={object}
        onConfirm={handleRevoke}
        onCancel={() => setToRevoke(null)}
      />
    </>
  );
}

/** @deprecated Use ManageAccessButton with resourceType="template" */
export function TemplateManageButton({ templateId }: { templateId: string }) {
  return <ManageAccessButton resourceType="template" resourceId={templateId} />;
}
